/**
 * Admin — Return-to-Work approvals.
 *
 * Department managers file "return-to-work" notifications when their
 * subordinates punch in after leave. This page is the admin queue:
 * approve → records `return_to_work_history` in the employee's HR file,
 * reject → notifies the manager with a note.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle, XCircle, HandWaving, ArrowsClockwise, CalendarCheck,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const STATUS_BADGE = {
  pending_admin: { ar: 'بانتظار الاعتماد', en: 'Pending',   cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved:      { ar: 'مُعتمَد',           en: 'Approved',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:      { ar: 'مرفوض',             en: 'Rejected',  cls: 'bg-red-100 text-red-800 border-red-200' },
};

export default function AdminReturnToWork({ embed = false }) {
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  // Action dialog
  const [action, setAction] = useState(null);      // { row, kind: 'approve' | 'reject' }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/return-to-work/pending`, { withCredentials: true }),
        axios.get(`${API}/return-to-work/all`, { withCredentials: true }),
      ]);
      setPending(p?.data || []);
      setHistory(a?.data || []);
    } catch (_e) {
      toast.error(isAr ? 'فشل جلب الطلبات' : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      const url = `${API}/return-to-work/${action.row.id}/${action.kind}`;
      await axios.post(url, { note: note.trim() }, { withCredentials: true });
      toast.success(action.kind === 'approve'
        ? (isAr ? 'تم الاعتماد ✓' : 'Approved ✓')
        : (isAr ? 'تم الرفض' : 'Rejected'));
      setAction(null);
      setNote('');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (isAr ? 'فشل تنفيذ الإجراء' : 'Action failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(isAr ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return iso; }
  };

  const renderRow = (r, showActions) => (
    <TableRow key={r.id} data-testid={`rtw-admin-row-${r.id}`}>
      <TableCell className="font-medium text-[#655dc6]">
        <div>{r.employee_name}</div>
        {r.employee_name_en && (
          <div className="text-[10px] text-[#655dc6]/60 leading-tight" dir="ltr">{r.employee_name_en}</div>
        )}
      </TableCell>
      <TableCell className="text-[#655dc6]/70">{r.department || '-'}</TableCell>
      <TableCell>
        {(() => {
          const cls = r.trigger === 'annual_leave'
            ? 'bg-sky-100 text-sky-800 border-sky-200'
            : r.trigger === 'sick_leave'
            ? 'bg-amber-100 text-amber-800 border-amber-200'
            : 'bg-rose-100 text-rose-800 border-rose-200';
          const label = r.trigger === 'annual_leave'
            ? (isAr ? 'إجازة سنوية' : 'Annual Leave')
            : r.trigger === 'sick_leave'
            ? (isAr ? 'إجازة مرضية' : 'Sick Leave')
            : (isAr ? 'غياب بدون عذر' : 'Unexcused Absence');
          return <Badge className={cls}>{label}</Badge>;
        })()}
      </TableCell>
      <TableCell className="text-[#655dc6]/70 whitespace-nowrap">
        {r.working_days
          ? (isAr ? <>{r.working_days} <span className="text-[10px] text-[#655dc6]/60">يوم عمل</span></>
                  : <>{r.working_days} <span className="text-[10px] text-[#655dc6]/60">wd</span></>)
          : '-'}
      </TableCell>
      <TableCell className="text-[#655dc6]/70">{r.last_leave_end}</TableCell>
      <TableCell className="text-[#655dc6]/70">{r.return_date}</TableCell>
      <TableCell className="text-[#655dc6]/70">{r.manager_name}</TableCell>
      <TableCell>
        <Badge className={STATUS_BADGE[r.status]?.cls || 'bg-gray-100'}>
          {STATUS_BADGE[r.status]?.[isAr ? 'ar' : 'en'] || r.status}
        </Badge>
      </TableCell>
      <TableCell className="text-[#655dc6]/60 text-xs">{fmtDateTime(r.created_at)}</TableCell>
      {showActions && (
        <TableCell>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => { setAction({ row: r, kind: 'approve' }); setNote(''); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 gap-1"
              data-testid={`rtw-approve-btn-${r.id}`}
            >
              <CheckCircle size={16} weight="bold" />
              {isAr ? 'اعتماد' : 'Approve'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setAction({ row: r, kind: 'reject' }); setNote(''); }}
              className="border-red-300 text-red-600 hover:bg-red-50 h-8 gap-1"
              data-testid={`rtw-reject-btn-${r.id}`}
            >
              <XCircle size={16} weight="bold" />
              {isAr ? 'رفض' : 'Reject'}
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  );

  const inner = (
    <>
      <Card className="border border-[#C4BFF0]">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-sm text-[#655dc6]/70">
              {isAr
                ? 'اعتماد طلبات مباشرة العمل بعد إجازة سنوية أو غياب بدون عذر لا يقل عن 7 أيام عمل.'
                : 'Approve return-to-work requests after annual leaves or unexcused absences of at least 7 working days.'}
            </p>
            <button
              onClick={load}
              className="text-[#655dc6]/70 hover:text-[#655dc6] p-2 rounded-md hover:bg-[#F2F0FA] border border-[#C4BFF0] transition-colors flex items-center gap-2"
              title={isAr ? 'تحديث' : 'Refresh'}
              data-testid="rtw-admin-refresh"
            >
              <ArrowsClockwise size={16} weight="bold" />
              <span className="text-sm">{isAr ? 'تحديث' : 'Refresh'}</span>
            </button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} dir={direction}>
            <TabsList>
              <TabsTrigger value="pending" data-testid="rtw-tab-pending" className="gap-2">
                <CalendarCheck size={16} weight="duotone" />
                {isAr ? `قيد الاعتماد (${pending.length})` : `Pending (${pending.length})`}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="rtw-tab-history">
                {isAr ? 'السجل الكامل' : 'Full History'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
                </div>
              ) : pending.length === 0 ? (
                <p className="text-center py-8 text-[#655dc6]/70" data-testid="rtw-empty-pending">
                  {isAr ? 'لا توجد طلبات قيد الاعتماد.' : 'No pending requests.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4BFF0]">
                        <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'القسم' : 'Department'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'المدة' : 'Duration'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'انتهاء الغياب' : 'Ended'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'تاريخ المباشرة' : 'Return Date'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'المُقدِّم' : 'Submitted By'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'تاريخ الإرسال' : 'Filed At'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'الإجراء' : 'Action'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{pending.map((r) => renderRow(r, true))}</TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-center py-8 text-[#655dc6]/70">{isAr ? 'لا توجد سجلات.' : 'No records.'}</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4BFF0]">
                        <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'القسم' : 'Department'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'المدة' : 'Duration'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'انتهاء الغياب' : 'Ended'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'تاريخ المباشرة' : 'Return Date'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'المُقدِّم' : 'Submitted By'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'تاريخ الإرسال' : 'Filed At'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{history.map((r) => renderRow(r, false))}</TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Approve / Reject dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) setAction(null); }}>
        <DialogContent dir={direction} className="sm:max-w-[460px]" data-testid="rtw-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
              {action?.kind === 'approve' ? (
                <><CheckCircle size={22} weight="duotone" className="text-emerald-600" /> {isAr ? 'اعتماد مباشرة العمل' : 'Approve Return to Work'}</>
              ) : (
                <><XCircle size={22} weight="duotone" className="text-red-600" /> {isAr ? 'رفض الطلب' : 'Reject Request'}</>
              )}
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-[#655dc6]/80">
              {action && (isAr
                ? <>الموظف <strong>{action.row.employee_name}</strong> — تاريخ المباشرة <strong>{action.row.return_date}</strong> بعد انتهاء إجازته في <strong>{action.row.last_leave_end}</strong>.</>
                : <><strong>{action.row.employee_name}</strong> — return date <strong>{action.row.return_date}</strong>, leave ended <strong>{action.row.last_leave_end}</strong>.</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs text-[#655dc6]/70 block">
              {action?.kind === 'reject'
                ? (isAr ? 'سبب الرفض (اختياري)' : 'Reason for rejection (optional)')
                : (isAr ? 'ملاحظة الاعتماد (اختياري)' : 'Approval note (optional)')}
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={400}
              data-testid="rtw-action-note"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setAction(null)} className="text-[#655dc6] hover:bg-[#EDE9FE]" data-testid="rtw-action-cancel">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              className={action?.kind === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="rtw-action-confirm"
            >
              {busy
                ? (isAr ? 'جارٍ التنفيذ…' : 'Working…')
                : (action?.kind === 'approve'
                    ? (isAr ? 'اعتماد' : 'Approve')
                    : (isAr ? 'رفض' : 'Reject'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Embedded inside another admin page (e.g. Leaves) — caller provides the
  // page-level wrapper (background, header, title). Just return the inner.
  if (embed) return inner;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="admin-rtw-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
          <HandWaving size={32} weight="duotone" className="text-amber-600" />
          {isAr ? 'مباشرة العمل بعد الإجازة' : 'Return to Work — Post Leave'}
        </h1>
        {inner}
      </main>
    </div>
  );
}
